import type { ReactElement } from 'react';
import { useEffect } from 'react';

import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import StringsPropVerification from '@web-platform/global-components-higher-order-components/StringsPropVerification';

import PageNotFoundHeader from './PageNotFoundHeader';
import PageNotFoundImage from './PageNotFoundImage';

import type { Logger } from '@web-platform/global-components-types/utils';

import STYLES from './NotFound.module.scss';

const stringsKeys = ['imageAltText', 'goBackLabel', 'message', 'title'] as const;

/**
 * @interface
 * @public
 * @property imageAltText - Alternative text for the 'Page not found' image.
 * @property goBackLabel - Label for the 'Go Back' button.
 * @property message - Informative message to display to the user.
 * @property title - Title to display for the page
 */
export type Strings = {
  [key in (typeof stringsKeys)[number]]: string;
};

/**
 * @public
 */
export interface NotFoundProps {
  /**
   * The URL to which the button should redirect.
   * @defaultValue "/"
   */
  buttonHref?: string;
  /**
   * Translated strings for the component.
   */
  strings: Strings;
  /**
   * A boolean determining if the environment is production.
   * @defaultValue false
   */
  isProductionEnv: boolean;
  /**
   * Logger object.
   */
  logger: Logger;
}

/**
 * The `NotFound` component displays our 404 not found message. It allows the user
 * to go back either to the homepage, or to a custom page url passed in as a prop, using a button.
 * @public
 *
 * @example
 * ```tsx
 * import NotFound from '@skyscanner-internal/global-components/NotFound';
 *
 * function App() {
 *   return (
 *     <NotFound
 *       buttonHref="https://www.skyscanner.net"
 *       strings={{
 *         imageAltText: 'Page not found.',
 *         goBackLabel: 'Go back',
 *         message: 'With Skyscanner you can go anywhere. But first you need to go back to the homepage.',
 *         title: 'Page not found'
 *       }}
 *       logger={logger}
 *       isProductionEnv={true}
 *     />
 *   );
 * }
 * ```
 */
const NotFound = ({
  buttonHref = '/',
  isProductionEnv = false,
  logger,
  strings,
}: NotFoundProps): ReactElement => {
  useEffect(() => {
    // This code runs once when the component mounts (assuming the logger does not change over the component's lifetime)
    logger.logOperationalEvent({
      eventName: 'not-found-loaded',
      component: 'not-found',
    });
  }, [logger]);

  return (
    <StringsPropVerification
      componentName={NotFound.name}
      isProductionEnv={isProductionEnv}
      logger={logger}
      strings={strings}
      stringsKeys={stringsKeys}
    >
      <div className={STYLES['not-found']}>
        <div className={STYLES['not-found__header']}>
          <PageNotFoundHeader />
        </div>
        <BpkText
          className={STYLES['not-found__title']}
          textStyle={TEXT_STYLES.heading1}
          tagName="h1"
        >
          {strings.title}
        </BpkText>
        <BpkText className={STYLES['not-found__message']}>{strings.message}</BpkText>
        <div className={STYLES['not-found__button']}>
          <BpkButton href={buttonHref}>{strings.goBackLabel}</BpkButton>
        </div>
        <div className={STYLES['not-found__img']}>
          <PageNotFoundImage altText={strings.imageAltText} />
        </div>
      </div>
    </StringsPropVerification>
  );
};

export default NotFound;
